import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Mail, 
  Send, 
  Clock, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Calendar,
  User,
  AlertCircle,
  Trash2,
  ExternalLink,
  UserPlus,
  Users
} from 'lucide-react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import { supabase } from '../../../shared/lib/supabase';
import { TopBar } from '../../../shared/components/navigation/TopBar';

interface TeamInvitation {
  id: string;
  team_id: string;
  invited_email: string;
  invited_by: string;
  role: 'member';
  status: 'pending' | 'sent' | 'accepted' | 'declined' | 'expired';
  invitation_token: string;
  expires_at: string;
  created_at: string;
  accepted_at: string | null;
  declined_at: string | null;
  teams: {
    id: string;
    name: string;
    avatar_url: string | null;
  };
  inviter: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export function TeamInvitationsPage() {
  const { user } = useAuth();
  const { refreshTeams, switchTeam } = useTeam();
  const [isLoading, setIsLoading] = useState(false);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [activeTab, setActiveTab] = useState<'pending' | 'accepted' | 'past'>('pending');
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  // Add search state
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user) {
      loadMyInvitations();
    }
  }, [user]);

  const loadMyInvitations = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Get the invitations with stored team data
      const { data: myInvitations, error } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('invited_email', user.email)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading invitations:', error);
        throw error;
      }

      // Get inviter data for each invitation
      const invitationsWithDetails = await Promise.all(
        (myInvitations || []).map(async (invitation) => {
          // Get inviter data
          const { data: inviterProfile } = await supabase
            .from('user_profiles')
            .select('first_name, last_name, email')
            .eq('user_id', invitation.invited_by)
            .single();

          // Use stored team data first, fallback to fetching if not available
          let teamData = null;
          if (invitation.team_name) {
            teamData = {
              id: invitation.team_id,
              name: invitation.team_name,
              avatar_url: invitation.team_avatar_url
            };
          } else {
            // Fallback: try to fetch team data
            const { data: fetchedTeamData } = await supabase
              .from('teams')
              .select('id, name, avatar_url')
              .eq('id', invitation.team_id)
              .single();
            teamData = fetchedTeamData;
          }

          return {
            ...invitation,
            teams: teamData || { id: invitation.team_id, name: 'Unknown Team', avatar_url: null },
            inviter: inviterProfile || {
              first_name: 'Unknown',
              last_name: 'User',
              email: 'unknown@user.com'
            }
          };
        })
      );

      setInvitations(invitationsWithDetails);
      
    } catch (error) {
      console.error('Error loading invitations:', error);
      toast.error('Failed to load invitations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitation: TeamInvitation) => {
    setProcessingIds(prev => new Set(prev).add(invitation.id));
    
    try {
      // Add user to team
      const { error: memberError } = await supabase
        .from('team_members')
        .insert({
          team_id: invitation.team_id,
          user_id: user!.id,
          role: invitation.role,
          joined_at: new Date().toISOString()
        });

      if (memberError) {
        throw memberError;
      }

      // Update invitation status
      const { error: updateError } = await supabase
        .from('team_invitations')
        .update({ 
          status: 'accepted',
          accepted_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      if (updateError) {
        console.error('Failed to update invitation status:', updateError);
      }

      // Refresh teams and switch to the new team
      await refreshTeams();
      if (invitation.teams?.id) {
        await switchTeam(invitation.teams.id);
      }
      
      toast.success(`Welcome to ${invitation.teams?.name || 'the team'}!`);
      loadMyInvitations(); // Reload to update the UI
      
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error('Failed to accept invitation. Please try again.');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitation.id);
        return newSet;
      });
    }
  };



  const handleRejectInvitation = async (invitationId: string) => {
    setProcessingIds(prev => new Set(prev).add(invitationId));
    
    try {
      // Update status to 'declined' and set declined_at timestamp
      const { data, error } = await supabase
        .from('team_invitations')
        .update({ 
          status: 'declined',
          declined_at: new Date().toISOString()
        })
        .eq('id', invitationId)
        .select();

      if (error) {
        console.error('❌ Error declining invitation:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error('No rows were updated. Check permissions.');
      }

      toast.success('Invitation declined');
      
      // Force a complete reload of invitations to get fresh data
      await loadMyInvitations();
      
      // Switch to Past tab to show the declined invitation
      setActiveTab('past');
      
    } catch (error) {
      console.error('❌ Full error declining invitation:', error);
      toast.error('Failed to decline invitation');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitationId);
        return newSet;
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'sent':
        return <Send className="w-5 h-5 text-blue-500" />;
      case 'accepted':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'declined':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'expired':
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
      default:
        return <Mail className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'sent':
        return 'Sent';
      case 'accepted':
        return 'Accepted';
      case 'declined':
        return 'Declined';
      case 'expired':
        return 'Expired';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString: string, isFuture = false) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (isFuture) {
      // For future dates (like expiration)
      if (diffInHours < 1) return 'Soon';
      if (diffInHours < 24) return `in ${diffInHours}h`;
      if (diffInHours < 168) return `in ${Math.floor(diffInHours / 24)}d`;
      return `on ${formatDate(dateString)}`;
    } else {
      // For past dates (like creation time)
      const pastDiffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
      if (pastDiffInHours < 1) return 'Just now';
      if (pastDiffInHours < 24) return `${pastDiffInHours}h ago`;
      if (pastDiffInHours < 168) return `${Math.floor(pastDiffInHours / 24)}d ago`;
      return formatDate(dateString);
    }
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  // Filter invitations based on active tab and search query
  const filteredInvitations = invitations.filter(invitation => {
    const expired = isExpired(invitation.expires_at);
    
    // First filter by tab
    let showInTab = false;
    if (activeTab === 'pending') {
      showInTab = invitation.status === 'pending' && !expired;
    } else if (activeTab === 'accepted') {
      showInTab = invitation.status === 'accepted';
    } else { // 'past' tab
      showInTab = invitation.status === 'declined' || invitation.status === 'expired' || expired;
    }
    
    // Then filter by search query
    if (!showInTab) return false;
    
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      invitation.teams?.name?.toLowerCase().includes(query) ||
      invitation.inviter?.first_name?.toLowerCase().includes(query) ||
      invitation.inviter?.last_name?.toLowerCase().includes(query) ||
      `${invitation.inviter?.first_name} ${invitation.inviter?.last_name}`.toLowerCase().includes(query)
    );
  });

  // Limit to 5 invitations per tab
  const displayedInvitations = filteredInvitations.slice(0, 5);
  const hasMoreInvitations = filteredInvitations.length > 5;

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <TopBar />
        <div className="pt-20 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Not Logged In</h2>
            <p className="text-gray-600">Please log in to view your team invitations.</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { 
      id: 'pending', 
      label: 'Pending', 
      icon: <Clock className="w-4 h-4" />,
      count: invitations.filter(i => i.status === 'pending' && !isExpired(i.expires_at)).length 
    },
    { 
      id: 'accepted', 
      label: 'Accepted', 
      icon: <CheckCircle className="w-4 h-4" />,
      count: invitations.filter(i => i.status === 'accepted').length 
    },
    { 
      id: 'past', 
      label: 'Past', 
      icon: <XCircle className="w-4 h-4" />,
      count: invitations.filter(i => i.status === 'declined' || i.status === 'expired' || isExpired(i.expires_at)).length 
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <TopBar />
      
      <div className="pt-20">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-sm">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Team Invitations</h1>
            </div>
            <p className="text-gray-600">
              Manage invitations you've received to join teams
            </p>
          </div>

          {/* Search Bar and Tab Navigation */}
          <div className="mb-8">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search invitations by team name or inviter..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-sm leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`
                      py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2
                      ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    {tab.icon}
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`
                        ml-2 px-2 py-1 rounded-sm text-xs font-medium
                        ${activeTab === tab.id 
                          ? 'bg-blue-100 text-blue-600' 
                          : 'bg-gray-100 text-gray-600'
                        }
                      `}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Invitations List */}
          <div className="bg-white rounded-sm border border-gray-200 shadow-sm">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center">
                  <RefreshCw className="animate-spin h-8 w-8 text-blue-600" />
                  <span className="ml-3 text-gray-600">Loading invitations...</span>
                </div>
              </div>
            ) : displayedInvitations.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  {activeTab === 'pending' && <Clock className="w-8 h-8 text-gray-400" />}
                  {activeTab === 'accepted' && <CheckCircle className="w-8 h-8 text-gray-400" />}
                  {activeTab === 'past' && <XCircle className="w-8 h-8 text-gray-400" />}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchQuery.trim() ? 'No matching invitations' : (
                    activeTab === 'pending' ? 'No pending invitations' :
                    activeTab === 'accepted' ? 'No accepted invitations' :
                    'No past invitations'
                  )}
                </h3>
                <p className="text-gray-600 max-w-sm mx-auto">
                  {searchQuery.trim() ? 
                    `No invitations match "${searchQuery}". Try a different search term.` : (
                    activeTab === 'pending' ? 'You don\'t have any pending team invitations at the moment.' :
                    activeTab === 'accepted' ? 'You haven\'t accepted any team invitations yet.' :
                    'No declined or expired invitations to show.'
                  )}
                </p>
              </div>
            ) : (
              <>
                <div className="divide-y divide-gray-200">
                  {displayedInvitations.map(invitation => {
                  const isProcessing = processingIds.has(invitation.id);
                  const expired = isExpired(invitation.expires_at);
                  
                  return (
                    <div key={invitation.id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          {/* Team Avatar */}
                          <div className="w-12 h-12 rounded-sm bg-gradient-to-r from-blue-500 to-amber-500 flex items-center justify-center flex-shrink-0">
                            {invitation.teams?.avatar_url ? (
                              <img 
                                src={invitation.teams.avatar_url} 
                                alt={invitation.teams.name}
                                className="w-full h-full object-cover rounded-sm"
                              />
                            ) : (
                              <span className="text-white text-lg font-bold">
                                {invitation.teams?.name?.charAt(0).toUpperCase() || 'T'}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">
                                Join "{invitation.teams?.name || 'Unknown Team'}"
                              </h3>
                              {getStatusIcon(expired ? 'expired' : invitation.status)}
                            </div>
                            
                            <div className="space-y-1 text-sm text-gray-600 mb-3">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                <span>
                                  Invited by {invitation.inviter?.first_name} {invitation.inviter?.last_name}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span>{formatRelativeTime(invitation.created_at)}</span>
                              </div>
                              {invitation.accepted_at && (
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  <span>Accepted {formatRelativeTime(invitation.accepted_at)}</span>
                                </div>
                              )}
                              {invitation.declined_at && (
                                <div className="flex items-center gap-2">
                                  <XCircle className="w-4 h-4 text-red-500" />
                                  <span>Declined {formatRelativeTime(invitation.declined_at)}</span>
                                </div>
                              )}
                              {!invitation.accepted_at && !expired && (
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  <span>Expires {formatRelativeTime(invitation.expires_at, true)}</span>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <span className={`
                                inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium
                                ${(expired && invitation.status === 'pending') ? 'bg-gray-100 text-gray-800' : ''}
                                ${invitation.status === 'pending' && !expired ? 'bg-yellow-100 text-yellow-800' : ''}
                                ${invitation.status === 'accepted' ? 'bg-green-100 text-green-800' : ''}
                                ${invitation.status === 'declined' ? 'bg-red-100 text-red-800' : ''}
                              `}>
                                {expired && invitation.status === 'pending' ? 'Expired' : getStatusText(invitation.status)}
                              </span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium bg-blue-100 text-blue-800">
                                {invitation.role}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        {invitation.status === 'pending' && !expired && (
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => handleRejectInvitation(invitation.id)}
                              disabled={isProcessing}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isProcessing ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <XCircle className="w-4 h-4" />
                              )}
                              Decline
                            </button>
                            <button
                              onClick={() => handleAcceptInvitation(invitation)}
                              disabled={isProcessing}
                              className="flex items-center gap-1 px-4 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isProcessing ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserPlus className="w-4 h-4" />
                              )}
                              Accept
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                                  })}
                </div>

                {/* Show more indicator */}
                {hasMoreInvitations && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200 text-center">
                    <p className="text-sm text-gray-600">
                      Showing 5 of {filteredInvitations.length} invitations
                      {searchQuery.trim() && ` matching "${searchQuery}"`}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Use the search bar above to find specific invitations
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-blue-50 rounded-sm border border-blue-200">
            <div className="flex">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">About Team Invitations</h3>
                <p className="mt-1 text-sm text-blue-700">
                  Team invitations expire after 7 days. When you accept an invitation, you'll be added to the team 
                  and can immediately start collaborating on projects. You can decline invitations you're not interested in.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}